import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import {
  Box, Chip, Typography, Avatar, Button,
} from '@mui/material'
import {
  Search as SearchIcon,
  ShoppingCart as OrderIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../components/shared/PageHeader'
import { ListToolbar } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { DataTable, type Column } from '../components/ui/DataTable'
import { themeColors } from '../app/layout/theme'
import { orderVerificationApi } from '../lib/api/orderVerificationApi'
import type { OrderVerificationRecord, OrderPaymentStatus, OrderVerificationStatus } from '../types/verificationTypes'

// ── Badge configs ──────────────────────────────────────────────────────────────

const PAYMENT_CONFIG: Record<OrderPaymentStatus, { bg: string; color: string; border: string }> = {
  'Pending':  { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'Partial':  { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Paid':     { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Refunded': { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
}

const VERIFY_CONFIG: Record<OrderVerificationStatus, { bg: string; color: string; border: string }> = {
  'Pending':      { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'Under Review': { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Approved':     { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Rejected':     { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
  'On Hold':      { bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
}

function PaymentBadge({ status }: { status: OrderPaymentStatus }) {
  const cfg = PAYMENT_CONFIG[status] ?? { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' }
  return <Chip label={status} size="small" sx={{ height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '7px' }, whiteSpace: 'nowrap' }} />
}

function VerifyBadge({ status }: { status: OrderVerificationStatus }) {
  const cfg = VERIFY_CONFIG[status] ?? { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' }
  return <Chip label={status} size="small" sx={{ height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '7px' }, whiteSpace: 'nowrap' }} />
}

function initials(name: string) { return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('') }
const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) { let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h); return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length] }
function formatDate(iso: string | null) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) }
function fmtCurrency(n: number) { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n) }

// ── Component ─────────────────────────────────────────────────────────────────

export default function OrderVerificationList() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_order_verif_page_size')
    return saved ? Number(saved) : 10
  })

  const paymentStatuses = orderVerificationApi.getPaymentStatuses()
  const verifyStatuses = orderVerificationApi.getVerificationStatuses()
  const assignedUsers = orderVerificationApi.getAssignedUsers()

  const filterDefs: FilterDef[] = useMemo(() => [
    {
      type: 'checkbox',
      key: 'payment',
      label: 'Payment Status',
      options: paymentStatuses.map(s => ({ label: s, value: s })),
    },
    {
      type: 'checkbox',
      key: 'verify',
      label: 'Verification Status',
      options: verifyStatuses.map(s => ({ label: s, value: s })),
    },
    {
      type: 'checkbox',
      key: 'user',
      label: 'Assigned User',
      options: assignedUsers.map(u => ({ label: u, value: u })),
    },
  ], [paymentStatuses, verifyStatuses, assignedUsers])

  const params = useMemo(() => ({
    search: search || undefined,
    paymentStatus: filters.checkboxes.payment?.length ? filters.checkboxes.payment[0] : undefined,
    verificationStatus: filters.checkboxes.verify?.length ? filters.checkboxes.verify[0] : undefined,
    page,
    pageSize,
  }), [search, filters, page, pageSize])

  const { data, isLoading } = useQuery({
    queryKey: ['order-verifications', params],
    queryFn: () => orderVerificationApi.getAll(params),
    staleTime: 30_000,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const hasFilters = countActiveFilters(filters) > 0 || !!search

  function clearFilters() {
    setSearch(''); setFilters(EMPTY_UNIFIED_FILTERS); setPage(1)
  }

  const columns: Column[] = [
    {
      id: 'orderId',
      label: 'Order ID',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.link, fontWeight: 700, fontFamily: 'monospace' }}>
          {row.orderId}
        </Typography>
      ),
    },
    {
      id: 'customerName',
      label: 'Customer Name',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: '10px', fontWeight: 700, bgcolor: avatarColor(row.customerName) }}>
            {initials(row.customerName)}
          </Avatar>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>{row.customerName}</Typography>
        </Box>
      ),
    },
    {
      id: 'companyName',
      label: 'Company Name',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{row.companyName}</Typography>
      ),
    },
    {
      id: 'productService',
      label: 'Product / Service',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => (
        <Chip label={row.productService} size="small"
          sx={{ height: 20, borderRadius: '4px', bgcolor: '#EDE7FF', color: themeColors.primary, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '8px' } }} />
      ),
    },
    {
      id: 'orderValue',
      label: 'Order Value',
      sortable: true,
      align: 'right' as const,
      render: (_, row: OrderVerificationRecord) => (
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: themeColors.text.primary }}>
          {fmtCurrency(row.orderValue)}
        </Typography>
      ),
    },
    {
      id: 'paymentStatus',
      label: 'Payment Status',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => <PaymentBadge status={row.paymentStatus} />,
    },
    {
      id: 'verificationStatus',
      label: 'Verification Status',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => <VerifyBadge status={row.verificationStatus} />,
    },
    {
      id: 'assignedUser',
      label: 'Assigned User',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Avatar sx={{ width: 22, height: 22, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(row.assignedUser) }}>
            {initials(row.assignedUser)}
          </Avatar>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{row.assignedUser}</Typography>
        </Box>
      ),
    },
    {
      id: 'createdDate',
      label: 'Created Date',
      sortable: true,
      render: (_, row: OrderVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{formatDate(row.createdDate)}</Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (_, row: OrderVerificationRecord) => (
        <Button
          size="small" variant="outlined"
          onClick={(e) => { e.stopPropagation(); navigate(`/order-verification/${row.id}`) }}
          sx={{ fontSize: '11px', height: 26, borderRadius: '6px', textTransform: 'none', borderColor: themeColors.border, color: themeColors.primary, '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}08` } }}
        >View</Button>
      ),
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Order Verification"
        breadcrumb="CRM"
      />

      {/* ── Pinned toolbar ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder="Search by order ID, customer, company, product..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          extraActions={
            hasFilters && (
              <Button size="small" onClick={clearFilters}
                sx={{ fontSize: '12px', height: 38, textTransform: 'none', color: themeColors.text.secondary, borderRadius: '8px' }}>
                Clear Filters
              </Button>
            )
          }
        />

        {/* Summary chips for current page */}
        {data && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
            {(['Pending', 'Under Review', 'Approved', 'Rejected', 'On Hold'] as OrderVerificationStatus[]).map(s => {
              const count = items.filter(r => r.verificationStatus === s).length
              if (!count) return null
              const cfg = VERIFY_CONFIG[s]
              return (
                <Chip key={s} label={`${s}: ${count}`} size="small"
                  sx={{ height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '8px' } }} />
              )
            })}
          </Box>
        )}
      </Box>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataTable
            columns={columns}
            rows={items}
            isLoading={isLoading}
            selectable={false}
            showRowNumbers={false}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/order-verification/${row.id}`)}
            emptyIcon={<OrderIcon />}
            emptyMessage={hasFilters ? 'No orders match your filters' : 'No order verifications found'}
            emptyHint={hasFilters ? 'Try adjusting your filters' : 'Order verification records will appear here'}
            pagination={{
              page,
              pageSize,
              total,
              totalPages,
              itemLabel: 'records',
              onPageChange: setPage,
              onPageSizeChange: (s) => { setPageSize(s); localStorage.setItem('crm_order_verif_page_size', String(s)); setPage(1) },
              pageSizeOptions: [10, 20, 50],
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
